/**
 * One more try when the gateway in front of Supabase answers instead of it.
 *
 * Cloudflare sits in front of every Supabase project. When it cannot reach the
 * origin for a moment it answers the request itself, with an HTML "502 Bad
 * Gateway" page. postgrest-js cannot parse that, hands back `{ message: <html> }`
 * as the error, and the page being rendered fails: MUCKA-WEB-CY on /schedule,
 * MUCKA-WEB-CP on /overview. In middleware the same blip on `auth.getUser()`
 * comes back as "no user", and a signed-in owner is sent to /login. These are
 * blips: the next request a moment later goes through.
 *
 * So a READ that gets a gateway page is sent once more, after a short pause.
 * The rules, and why each one is there:
 *
 * - **GET and HEAD only, never a write.** A POST or PATCH can reach Postgres,
 *   commit, and still come back to us as a 502 because the gateway lost the
 *   reply rather than the request. Sending it again would create the invoice
 *   twice. `.rpc()` is a POST by default, so it is never retried either.
 *
 * - **An HTML page, not any 5xx.** PostgREST only ever replies in JSON, so HTML
 *   is by definition the gateway (the rule `isTransportFailureMessage` already
 *   applies). A JSON 500 is the database answering — a bad query, a function
 *   that raised — and asking again gets the same answer.
 *
 * - **Not 503 or 520 on PostgREST.** postgrest-js already retries those itself
 *   on reads (3 times, 1s/2s/4s). This wrapper is its `fetch`, so doing it here
 *   too would run every attempt of its loop twice: 8 requests, not 4. The gap
 *   it leaves is 502, which is the one in both Sentry issues. Auth and storage
 *   have no such loop, so they keep these.
 *
 * - **Only when the failure was fast.** A 502 normally arrives in milliseconds.
 *   A 504/524 is Cloudflare giving up after ~100s; waiting that long a second
 *   time would spend a third of the 300s function limit on one read.
 *
 * - **Once, then a 30s breaker.** If the retry also gets a gateway page this is
 *   an outage, not a blip, and every further read in this instance fails at
 *   once (as it did before this existed) until the window passes. Without it a
 *   cron looping over 500 accounts during an outage would add a pause and a
 *   round-trip to every one of them and run into the limit instead of failing.
 *
 * When the retry fails too, its response goes back untouched, so postgrest-js
 * builds exactly the error it built before and the caller's
 * `dbError(error, …)` throws the same `DbError` — named, fingerprinted by
 * query, its HTML page summarised to `[HTML error page: 502 Bad Gateway]` by
 * `before-send`.
 *
 * Cost against the 300s limit: in the usual case one pause plus one fast
 * round-trip, about half a second, paid only by a request that would otherwise
 * have failed. Worst case for a single read is FAST_FAILURE_MS + RETRY_DELAY_MS
 * on top of what one attempt can already take, and the breaker stops that
 * repeating more than once per 30s per instance.
 *
 * Shared by the server, admin, middleware and browser clients. In a browser a
 * gateway page without CORS headers never reaches here at all: fetch throws,
 * and postgrest-js's own network-error retry takes it.
 */

const RETRY_DELAY_MS = 400;
const FAST_FAILURE_MS = 5_000;
const BREAKER_MS = 30_000;

/** Statuses postgrest-js retries on its own reads (`RETRYABLE_STATUS_CODES`). */
const POSTGREST_RETRIES = new Set([503, 520]);

type FetchFn = typeof fetch;

interface GatewayRetryOptions {
  /** The fetch to wrap. Defaults to whatever global `fetch` is at call time. */
  baseFetch?: FetchFn;
  delayMs?: number;
  now?: () => number;
}

function requestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  const method = init?.method ?? (typeof Request !== 'undefined' && input instanceof Request ? input.method : 'GET');
  return method.toUpperCase();
}

function requestPath(input: RequestInfo | URL): string {
  const href = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  try {
    // The pathname only: the query string carries filter values (ids, emails).
    return new URL(href).pathname;
  } catch {
    return '';
  }
}

/** Cloudflare's own error page, as opposed to anything Supabase sent. */
export function isGatewayPage(res: Response): boolean {
  if (res.status < 500) return false;
  return (res.headers.get('content-type') ?? '').toLowerCase().includes('text/html');
}

/** Would this response, to this request, be retried (breaker and timing aside)? */
export function isRetryableGatewayFailure(method: string, path: string, res: Response): boolean {
  if (method !== 'GET' && method !== 'HEAD') return false;
  if (!isGatewayPage(res)) return false;
  if (path.startsWith('/rest/v1/') && POSTGREST_RETRIES.has(res.status)) return false;
  return true;
}

export function createGatewayRetryFetch(options: GatewayRetryOptions = {}): FetchFn {
  const delayMs = options.delayMs ?? RETRY_DELAY_MS;
  const now = options.now ?? Date.now;
  const send: FetchFn = (input, init) => (options.baseFetch ?? globalThis.fetch)(input, init);
  let gatewayDownUntil = 0;

  return async (input, init) => {
    const started = now();
    const res = await send(input, init);

    const method = requestMethod(input, init);
    const path = requestPath(input);
    if (!isRetryableGatewayFailure(method, path, res)) return res;
    if (now() - started > FAST_FAILURE_MS) return res;
    if (now() < gatewayDownUntil) return res;

    console.warn(`[supabase-gateway-retry] ${res.status} gateway page on ${method} ${path}, retrying once`);
    await new Promise((resolve) => setTimeout(resolve, delayMs));
    if (init?.signal?.aborted) return res;

    // Free the first response's connection; nobody will read it now.
    res.body?.cancel().catch(() => {});

    const retried = await send(input, init);
    if (isGatewayPage(retried)) {
      gatewayDownUntil = now() + BREAKER_MS;
      console.warn(`[supabase-gateway-retry] retry got ${retried.status} too on ${method} ${path}; no retries for ${BREAKER_MS / 1000}s`);
    }
    return retried;
  };
}

/** The one instance every Supabase client uses, so the breaker is shared. */
export const supabaseFetch: FetchFn = createGatewayRetryFetch();
